import GdprUtil from 'utils/gdpr/GdprUtil';
import GdprFeature from 'models/gdpr/Feature';
import ModelSynchronizer from 'utils/ModelSynchronizer';
import GdprUploadConsentModalView from 'views/gdpr/GdprUploadConsentModalView';
import GdprConfirmationModalView from 'views/gdpr/GdprConfirmationModalView';
import ModalPopupView from 'views/main/Modal/ModalPopupView';
import Preferences from 'models/preferences/Preferences';
import Localizer from 'localizer';
import Backbone from 'backbone';

// These methods are used as predicates in dozens of places to check on the status of the user's upload consent and prompts them if consent is required for that specific action
// > this.gdprUploadConsentFetching prevents double clicks on UI elements that use the methods from calling the GDPR service twice
// > previously, a double click caused a race condition where the second call was fired before the first one returned and erroneously opened the consent modal when it wasn't necessary

export default {
    handleNoConsentGranted: function(navigateToPreviousPage, rightToRectify, options) {
        if (this.gdprUploadConsentFetching) {
            return;
        }
        this.gdprUploadConsentFetching = true;

        let modelSynchronizer = new ModelSynchronizer();

        this.gdprFeatureUpload = new GdprFeature({
            feature: GdprFeature.Features.UPLOAD
        });
        modelSynchronizer.addModel({ model: this.gdprFeatureUpload, required: true });

        modelSynchronizer.bind(
            ModelSynchronizer.Events.SYNCHRONIZED,
            () => {
                this.gdprUploadConsentFetching = false;

                const userOption = this.gdprFeatureUpload.get('userOption');
                const consentEnforcementState = this.gdprFeatureUpload.get('consentEnforcementState');
                const sourceType = this.gdprFeatureUpload.get('sourceType');
                const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');
                const isNotConsentEnforcementStateNone = consentEnforcementState !== GdprUtil.ENFORCEMENT_TYPE.NONE;

                if (
                    isNotConsentEnforcementStateNone &&
                    !GdprUtil.isFeatureForUserOn(userOption, consentEnforcementState, sourceType)
                ) {
                    // If a data deletion is in progress, show a modal informing the user and telling them to try again later
                    if (
                        deleteStatus === GdprUtil.DELETE_STATUS.IN_PROGRESS ||
                        deleteStatus === GdprUtil.DELETE_STATUS.CONFIRMED
                    ) {
                        this.dataDeleteInProgress(navigateToPreviousPage);
                    } else if (!rightToRectify) {
                        this.openGDPRModal(navigateToPreviousPage, true, options);
                    }
                }
            },
            this
        );

        modelSynchronizer.fetchModels();
    },

    handleGdprUploadConsent: function(actionCallback, isUserOptIn, failActionCallback) {
        if (this.gdprUploadConsentFetching) {
            return;
        }
        this.gdprUploadConsentFetching = true;

        let modelSynchronizer = new ModelSynchronizer();

        this.gdprFeatureUpload = new GdprFeature({
            feature: GdprFeature.Features.UPLOAD
        });
        modelSynchronizer.addModel({ model: this.gdprFeatureUpload, required: true });

        this.userActionCallback = actionCallback;

        modelSynchronizer.bind(
            ModelSynchronizer.Events.SYNCHRONIZED,
            () => {
                this.gdprUploadConsentFetching = false;

                const userOption = this.gdprFeatureUpload.get('userOption');
                const consentEnforcementState = this.gdprFeatureUpload.get('consentEnforcementState');
                const sourceType = this.gdprFeatureUpload.get('sourceType');
                const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');
                const isNotConsentEnforcementStateNone = consentEnforcementState !== GdprUtil.ENFORCEMENT_TYPE.NONE;

                if (
                    isNotConsentEnforcementStateNone &&
                    !GdprUtil.isFeatureForUserOn(userOption, consentEnforcementState, sourceType)
                ) {
                    // If a data deletion is in progress, show a modal informing the user and telling them to try again later
                    if (
                        deleteStatus === GdprUtil.DELETE_STATUS.IN_PROGRESS ||
                        deleteStatus === GdprUtil.DELETE_STATUS.CONFIRMED
                    ) {
                        this.dataDeleteInProgress();
                    } else {
                        this.openGDPRModal(false, !!isUserOptIn, { failActionCallback });
                    }
                } else {
                    !!this.userActionCallback ? this.userActionCallback() : '';
                }
            },
            this
        );
        modelSynchronizer.bind(
            ModelSynchronizer.Events.SYNCHRONIZE_FAILED,
            () => {
                this.gdprUploadConsentFetching = false;

                !!failActionCallback ? failActionCallback() : '';
            },
            this
        );

        modelSynchronizer.fetchModels();
    },

    handleGdprUploadConsentForThirdParty: function(userGrantActionCallback, userRevokeActionCallback) {
        if (this.gdprUploadConsentFetching) {
            return;
        }
        this.gdprUploadConsentFetching = true;

        const modelSynchronizer = new ModelSynchronizer();

        this.gdprFeatureUpload = new GdprFeature({
            feature: GdprFeature.Features.UPLOAD
        });
        modelSynchronizer.addModel({ model: this.gdprFeatureUpload, required: true });

        this.userGrantActionCallback = userGrantActionCallback;
        this.userRevokeActionCallback = userRevokeActionCallback;

        modelSynchronizer.bind(
            ModelSynchronizer.Events.SYNCHRONIZED,
            () => {
                this.gdprUploadConsentFetching = false;

                const userOption = this.gdprFeatureUpload.get('userOption');
                const consentEnforcementState = this.gdprFeatureUpload.get('consentEnforcementState');
                const sourceType = this.gdprFeatureUpload.get('sourceType');
                const deleteStatus = this.gdprFeatureUpload.get('deleteStatus');
                const isNotConsentEnforcementStateNone = consentEnforcementState !== GdprUtil.ENFORCEMENT_TYPE.NONE;

                // If a data deletion is in progress, show a modal informing the user and telling them to try again later
                if (
                    deleteStatus === GdprUtil.DELETE_STATUS.IN_PROGRESS ||
                    deleteStatus === GdprUtil.DELETE_STATUS.CONFIRMED
                ) {
                    this.dataDeleteInProgress();
                } else if (
                    isNotConsentEnforcementStateNone &&
                    !GdprUtil.isFeatureForUserOn(userOption, consentEnforcementState, sourceType)
                ) {
                    this.openGDPRModalWithoutDataStorageCheck();
                } else {
                    !!this.userGrantActionCallback ? this.userGrantActionCallback() : '';
                }
            },
            this
        );

        modelSynchronizer.fetchModels();
    },

    openGDPRModal: function(navigateToPreviousPage, isUserOptIn, options = {}) {
        const failActionCallback = options.failActionCallback || (() => {});

        let gdprUploadConsentModalView = new GdprUploadConsentModalView({
            isUserOptIn: isUserOptIn,
            gdprFeatureUpload: this.gdprFeatureUpload,
            onCloseButtonClicked: () => {
                if (navigateToPreviousPage) {
                    this.onNavigateToPreviousPage();
                }
            },
            onUploadRevoked: { error: failActionCallback },
            onUploadConsented: { error: failActionCallback },
            onCancelUploadDataModalView: this.userActionCallback && this.userActionCallback.bind(this),
            onAllowDeviceToUploadData: this.userActionCallback && this.userActionCallback.bind(this),
            navigateToPreviousPage: navigateToPreviousPage,
            options: options
        });

        gdprUploadConsentModalView.gdprUpload();
    },

    openDeviceUploadModal: function(actionCallback, failCallback) {
        this.userActionCallback = actionCallback;

        let gdprUploadConsentModalView = new GdprUploadConsentModalView({
            isUserOptIn: true,
            gdprFeatureUpload: this.gdprFeatureUpload,
            onCloseButtonClicked: () => {},
            onUploadRevoked: this.onUploadRevoked ? this.onUploadRevoked.bind(this) : '',
            onCancelUploadDataModalView: this.userActionCallback && this.userActionCallback.bind(this),
            onAllowDeviceToUploadData: this.userActionCallback && this.userActionCallback.bind(this),
            navigateToPreviousPage: false
        });

        gdprUploadConsentModalView.handleGarminDeviceUpload(failCallback);
    },

    onNavigateToPreviousPage: function() {
        Backbone.history.history.back();
    },

    openGDPRModalWithoutDataStorageCheck: function() {
        let gdprUploadConsentModalView = new GdprUploadConsentModalView({
            gdprFeatureUpload: this.gdprFeatureUpload
        });

        gdprUploadConsentModalView.gdprUploadWithoutAccountDeviceSyncPref(
            this.userGrantActionCallback,
            this.userRevokeActionCallback
        );
    },

    // Creates a confirmation modal to inform the user the data purge is underway
    dataDeleteInProgress: function(navigateToPreviousPage) {
        const that = this;

        const gdprConfirmationModalView = new GdprConfirmationModalView({
            modalTopTitle: Localizer.localize('try_again_later'),
            modalBody: Localizer.localize('data_deletion_in_progress'),
            showFooter: false,
            saveBtn: Localizer.localize('button_ok')
        });

        const modalPopupView = new ModalPopupView({
            view: gdprConfirmationModalView,
            useCustomBody: true,
            showHeader: false,
            showFooter: false,
            // "OK" button
            onSaveButtonClicked: function() {
                modalPopupView.close(() => {
                    if (navigateToPreviousPage) {
                        that.onNavigateToPreviousPage();
                    }
                });
            },
            onCloseButtonClicked: function() {
                modalPopupView.close(() => {
                    if (navigateToPreviousPage) {
                        that.onNavigateToPreviousPage();
                    }
                });
            }
        });

        modalPopupView.render();
    }
};
